// `evidence/source` — the personalised source-recommendations read, carved from
// `src/hooks/query/useQuerySourceRecommendations.ts` (now a bodied delegate).
//
// `GET /evidence/source-recommendations` returns sources matched to the user's
// country and specialty.
//
// S1: transport through the `@heidi/api/client` registry rather than the
// `@/utils/scribeHTTP` module singleton — the same instance, so the request is
// unchanged.
//
// S-enabled (cohort S2 + the flag ledger): the pre-move gate was
// `!!token && enabled && !isKillSwitchOn`, mixing a Rematch auth read and a
// KILL-SWITCH flag getter with the caller's own `enabled` argument inside a data
// hook. The two app terms collapse into the delegate's boolean; the caller's
// argument keeps its own name and position, so no call site changes.
//
// THE KILL SWITCH IS THE POINT OF THE ORDERING. `useIsSourceRecommendationsKillSwitchEnabled`
// exists to stop this request in production without a deploy, so it must remain
// able to force the gate to `false` regardless of what a caller passes. Folding
// it into the delegate's single boolean is what preserves that: a caller cannot
// pass an `enabled` that outvotes it, because the delegate `&&`s them in the same
// order the legacy expression did. No flag getter is rewritten to the runtime
// door: the ledger forbids it and this carve does not do it.
import { useQuery } from '@tanstack/react-query';

import {
  SOURCE_RECOMMENDATIONS_QUERY_KEY,
  type SourceRecommendationsResponse,
} from '../../contracts';
import { getSourceRecommendations } from '../api/sources';

export interface SourceRecommendationsReadOptions {
  /**
   * App-resolved gate, already reduced to one decision — the caller's own
   * `enabled` argument AND-ed with the auth token and the kill switch by the
   * delegate, in the legacy order.
   */
  enabled: boolean;
}

export function useQuerySourceRecommendations({
  enabled,
}: SourceRecommendationsReadOptions) {
  return useQuery<SourceRecommendationsResponse>({
    queryKey: [SOURCE_RECOMMENDATIONS_QUERY_KEY],
    queryFn: getSourceRecommendations,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}
